import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import GTM from "../../GTM.js";
import PropTypes from "prop-types";

function HelpContent(props) {
    const {
        showHelpContent,
        content,
        handleBack,
        setShowHelpContent,
        handleHelpClose,
    } = props?.helpSectionProps || {};

    const gtm = new GTM(null);
    const [activeAccordion, setActiveAccordion] = useState(null);
    const [animationClass, setAnimationClass] = useState("slide-in");

    const handleAccordionToggle = useCallback(
        (faq, index) => {
            const isExpanding = index !== activeAccordion;

            setActiveAccordion((prevState) =>
                index === prevState ? null : index
            );

            let description = faq.Description;
            if (Array.isArray(description?.Points)) {
                description = description.Points.join("");
            }

            if (isExpanding) {
                gtm.Event(
                    "help section",
                    "accordion",
                    "expand",
                    description,
                    "help",
                    content?.Name,
                    "help_item_click",
                    ""
                );
            }
        },
        [activeAccordion, content?.Name]
    );

    const handleClose = () => {
        setAnimationClass("slide-out");
        handleHelpClose();
    };

    const handleBackHelp = () => {
        setAnimationClass("slide-out");
        setTimeout(() => {
            setShowHelpContent(false);
            handleBack();
        }, 1100);
    };

    const renderDescription = (description) => {
        if (typeof description === "string") {
            return (
                <span>
                    {description.split("\n").map((line, index) => (
                        <React.Fragment key={index}>
                            {line}
                            <br />
                        </React.Fragment>
                    ))}
                </span>
            );
        }
        if (Array.isArray(description?.Points)) {
            return (
                <ul className="faq-ul">
                    {description.Points.map((point, pointIndex) => (
                        <li key={pointIndex} className="faq-list">
                            {point}
                        </li>
                    ))}
                </ul>
            );
        }
        return <></>;
    };

    const handleKeyDown = (event) => {
        const modalBody = document.querySelector(".modal-body");
        if (!modalBody) return;

        if (event.key === "ArrowDown") {
            event.preventDefault();
            modalBody.scrollBy({ top: 1, behavior: "smooth" });
        }

        if (event.key === "ArrowUp") {
            event.preventDefault();
            modalBody.scrollBy({ top: -1, behavior: "smooth" });
        }
    };

    useEffect(() => {
        if (showHelpContent) {
            window.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [showHelpContent]);

    return (
        <Modal
            show={showHelpContent}
            dialogClassName="help-section"
            size="xl"
            backdrop="true"
            onHide={handleClose}
            backdropClassName="helpContent-backdrop"
            className={animationClass}
            aria-labelledby="help-section-title"
        >
            <div className="help-section-container">
                <Modal.Header style={{ justifyContent: "space-between" }}>
                    {/* Back Button */}
                    <button
                        type="button"
                        className="help-buttons align-items-center border-0 bg-transparent"
                        style={{ fontWeight: 700 }}
                        onClick={handleBackHelp}
                    >
                        <i
                            className="fa-solid fa-angle-left"
                            aria-hidden="true"
                        />
                        <span className="ml-8p">Back</span>
                    </button>

                    {/* Close Button */}
                    <button
                        type="button"
                        className="help-buttons align-items-center border-0 bg-transparent"
                        style={{ paddingRight: 0 }}
                        onClick={handleClose}
                    >
                        <i
                            className="fa-solid fa-xmark"
                            aria-hidden="true"
                        />
                        <span className="ml-8p">Close</span>
                    </button>
                </Modal.Header>

                <Modal.Body
                    style={{
                        padding: 0,
                        maxHeight: "calc(100vh - 140px)",
                        overflowY: "auto",
                    }}
                >
                    <div className="pr-32p">
                        <div
                            id="help-section-title"
                            className="header pt-20p"
                        >
                            {content?.Name}
                        </div>

                        <div style={{ width: 604 }}>
                            {content?.Content.map((faq, index) => {
                                const isOpen = activeAccordion === index;
                                const accordionId = `accordion-${index}`;
                                const panelId = `panel-${index}`;

                                return (
                                    <div className="accordion" key={index}>
                                        <button
                                            type="button"
                                            className="d-flex px-20p faq-subtext cursor-pointer btn-reset w-100 text-start border-0 bg-transparent"
                                            onClick={() =>
                                                handleAccordionToggle(
                                                    faq,
                                                    index
                                                )
                                            }
                                            aria-expanded={isOpen}
                                            aria-controls={panelId}
                                            id={accordionId}
                                        >
                                            <h2 className="section-heading mb-0p">
                                                <div className="faq-header">
                                                    {faq.Header}
                                                </div>
                                            </h2>

                                            <i
                                                className={`fa-solid fa-${
                                                    isOpen ? "minus" : "plus"
                                                } accordion-icon ml-8p`}
                                                aria-hidden="true"
                                            />
                                        </button>

                                        {isOpen && (
                                            <section
                                                id={panelId}
                                                aria-label={panelId}
                                                aria-labelledby={accordionId}
                                                className="faq-subtext-collapse accordion-collapse show"
                                            >
                                                <div className="faq-card card-body">
                                                    <div className="text-align-justify">
                                                        {renderDescription(
                                                            faq.Description
                                                        )}

                                                        {faq.ImageUrl && (
                                                            <img
                                                                src={
                                                                    faq.ImageUrl
                                                                }
                                                                alt="Help content visual"
                                                                style={{
                                                                    maxWidth:
                                                                        "100%",
                                                                    marginTop:
                                                                        "10px",
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </section>
                                        )}

                                        <hr className="mb-20p mx-20p faq-divider" />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </Modal.Body>
            </div>
        </Modal>
    );
}

HelpContent.propTypes = {
    helpSectionProps: PropTypes.shape({
        showHelpContent: PropTypes.bool.isRequired,
        setShowHelpContent: PropTypes.func.isRequired,
        handleHelpClose: PropTypes.func.isRequired,
        handleBack: PropTypes.func.isRequired,
        content: PropTypes.shape({
            Name: PropTypes.string.isRequired,
            Content: PropTypes.arrayOf(
                PropTypes.shape({
                    Header: PropTypes.string.isRequired,
                    Description: PropTypes.oneOfType([
                        PropTypes.string,
                        PropTypes.shape({
                            Points: PropTypes.arrayOf(PropTypes.string),
                        }),
                    ]).isRequired,
                    LineBreak: PropTypes.bool,
                    ImageUrl: PropTypes.string,
                })
            ).isRequired,
            IconHtml: PropTypes.string,
            Order: PropTypes.number,
            ContentType: PropTypes.string,
        }).isRequired,
    }).isRequired,
};

export default HelpContent;